/**
 * 指纹生成器
 * 移植自 xhshow-go/xhshow/fingerprint.go
 */

import { createHash, randomBytes } from 'crypto';
import RC4 from 'crypto-js/rc4.js';
import Latin1 from 'crypto-js/enc-latin1.js';
import { encodeCustomBase64 } from '../utils/index.js';
import { B1_SECRET_KEY } from '../config.js';
import {
  COLOR_DEPTH_OPTIONS,
  DEVICE_MEMORY_OPTIONS,
  CORE_OPTIONS,
  BROWSER_PLUGINS,
  VOICE_HASH_OPTIONS,
  FONTS,
} from './data.js';
import {
  weightedRandomChoice,
  getRendererInfo,
  getScreenConfig,
  generateCanvasHash,
  generateWebglHash,
} from './helpers.js';
import type { FingerprintData, CookieDict } from '../types/index.js';

export class FingerprintGenerator {
  constructor() {
    // B1 密钥在 generateB1 方法中使用
  }

  /**
   * 自定义 URL 编码 (实现 urllib.parse.quote(s, safe="!*'()~_-"))
   */
  private customQuote(s: string): string {
    const safeChars = "!*'()~_-";
    let buf = '';
    for (let i = 0; i < s.length; i++) {
      const b = s.charCodeAt(i);
      const char = s[i];
      if (
        (b >= 97 && b <= 122) || // a-z
        (b >= 65 && b <= 90) || // A-Z
        (b >= 48 && b <= 57) || // 0-9
        char === '.'
      ) {
        buf += char;
      } else if (safeChars.includes(char)) {
        buf += char;
      } else {
        buf += '%' + b.toString(16).toUpperCase().padStart(2, '0');
      }
    }
    return buf;
  }

  /**
   * 生成 B1 签名
   */
  generateB1(fp: FingerprintData): string {
    // 提取 B1 所需的特定字段
    const keys = [
      'x33',
      'x34',
      'x35',
      'x36',
      'x37',
      'x38',
      'x39',
      'x42',
      'x43',
      'x44',
      'x45',
      'x46',
      'x48',
      'x49',
      'x50',
      'x51',
      'x52',
      'x82',
    ];
    const b1Fp: Record<string, any> = {};
    for (const k of keys) {
      if (k in fp) {
        b1Fp[k] = fp[k];
      }
    }

    // JSON 序列化（不转义HTML字符）
    const jsonStr = JSON.stringify(b1Fp);

    // RC4 加密 (使用 crypto-js)
    const encrypted = RC4.encrypt(jsonStr, B1_SECRET_KEY);
    const ciphertext = encrypted.ciphertext;

    // 转换为字节数组（Latin-1 编码）
    const ciphertextStr = Latin1.stringify(ciphertext);

    // Quote (将字节视为 latin1 字符)
    const encodedUrl = this.customQuote(ciphertextStr);

    // 重建循环 (Python 逻辑的拆分还原)
    const parts = encodedUrl.split('%');
    const b: number[] = [];

    // 跳过第一部分（索引 0）
    for (let i = 1; i < parts.length; i++) {
      const part = parts[i];
      if (part.length < 2) {
        continue;
      }

      // 解析前两个字符为十六进制
      const hexStr = part.substring(0, 2);
      const val = parseInt(hexStr, 16);
      if (!isNaN(val)) {
        b.push(val);
      }

      // 追加剩余部分作为字节
      for (let j = 2; j < part.length; j++) {
        b.push(part.charCodeAt(j));
      }
    }

    return encodeCustomBase64(new Uint8Array(b));
  }

  /**
   * 生成完整指纹
   */
  generate(cookies: CookieDict, userAgent: string): FingerprintData {
    // 构建 Cookie 字符串
    const cookieParts: string[] = [];
    for (const [k, v] of Object.entries(cookies)) {
      cookieParts.push(`${k}=${v}`);
    }
    const cookieString = cookieParts.join('; ');

    const screen = getScreenConfig();

    // 加权随机选择是否无痕模式
    const isIncognito = weightedRandomChoice(['true', 'false'], [0.95, 0.05]);

    const { vendor, renderer } = getRendererInfo();

    const x78_y = Math.floor(Math.random() * 101) + 2350; // 2350 到 2450

    // 生成 x53 (32 字节随机数的 MD5)
    const token = randomBytes(32);
    const x53Hash = createHash('md5').update(token).digest('hex');
    const x53 = x53Hash;

    // x36 随机整数 1-20
    const x36 = (Math.floor(Math.random() * 20) + 1).toString();

    // x44 时间戳
    const x44 = Date.now().toString();

    const fp: FingerprintData = {
      x1: userAgent,
      x2: 'false',
      x3: 'zh-CN',
      x4: weightedRandomChoice(COLOR_DEPTH_OPTIONS.values, COLOR_DEPTH_OPTIONS.weights),
      x5: weightedRandomChoice(DEVICE_MEMORY_OPTIONS.values, DEVICE_MEMORY_OPTIONS.weights),
      x6: '24',
      x7: `${vendor},${renderer}`,
      x8: weightedRandomChoice(CORE_OPTIONS.values, CORE_OPTIONS.weights),
      x9: `${screen.width};${screen.height}`,
      x10: `${screen.availWidth};${screen.availHeight}`,
      x11: '-480',
      x12: 'Asia/Shanghai',
      x13: isIncognito,
      x14: isIncognito,
      x15: isIncognito,
      x16: 'false',
      x17: 'false',
      x18: 'un',
      x19: 'Win32',
      x20: '',
      x21: BROWSER_PLUGINS,
      x22: generateWebglHash(),
      x23: 'false',
      x24: 'false',
      x25: 'false',
      x26: 'false',
      x27: 'false',
      x28: '0,false,false',
      x29: '4,7,8',
      x30: 'swf object not loaded',
      x33: '0',
      x34: '0',
      x35: '0',
      x36: x36,
      x37: '0|0|0|0|0|0|0|0|0|1|0|0|0|0|0|0|0|0|1|0|0|0|0|0',
      x38: '0|0|1|0|1|0|0|0|0|0|1|0|1|0|1|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0|0',
      x39: 0,
      x40: '0',
      x41: '0',
      x42: '3.4.4',
      x43: generateCanvasHash(),
      x44: x44,
      x45: '__SEC_CAV__1-1-1-1-1|__SEC_WSA__|',
      x46: 'false',
      x47: '1|0|0|0|0|0',
      x48: '',
      x49: '{list:[],type:}',
      x50: '',
      x51: '',
      x52: '',
      x55: '380,380,360,400,380,400,420,380,400,400,360,360,440,420',
      x56: `${vendor}|${renderer}|${generateWebglHash()}|35`,
      x57: cookieString,
      x58: '180',
      x59: '2',
      x60: '63',
      x61: '1291',
      x62: '2047',
      x63: '0',
      x64: '0',
      x65: '0',
      x66: {
        referer: '',
        location: 'https://www.xiaohongshu.com/explore',
        frame: 0,
      },
      x67: '1|0',
      x68: '0',
      x69: '326|1292|30',
      x70: ['location'],
      x71: 'true',
      x72: 'complete',
      x73: '1191',
      x74: '0|0|0',
      x75: 'Google Inc.',
      x76: 'true',
      x77: '1|1|1|1|1|1|1|1|1|1',
      x78: {
        x: 0,
        y: x78_y,
        left: 0,
        right: 290.828125,
        bottom: x78_y + 18,
        height: 18,
        top: x78_y,
        width: 290.828125,
        font: FONTS,
      },
      x82: '_0x17a2|_0x1954',
      x31: '124.04347527516074',
      x79: '144|599565058866',
      x53: x53,
      x54: VOICE_HASH_OPTIONS,
      x80: '1|[object FileSystemDirectoryHandle]',
    };

    return fp;
  }
}
